import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi, type Track } from '../hooks/useApi';
import { Loader2, BookOpen, ChevronRight } from 'lucide-react';
import StreakPanel from '../components/StreakPanel';
import StreakRiskBanner from '../components/StreakRiskBanner';
import DailyWorkout from '../components/DailyWorkout';
import DailyInsight from '../components/DailyInsight';
import CohortWidget from '../components/CohortWidget';
import BuddyWidget from '../components/BuddyWidget';

interface ProgressItem {
  lesson_id: string;
  status: string;
  last_updated: string;
  lesson_title: string;
  lesson_slug: string;
  course_title: string;
  course_slug: string;
  track_title: string;
  track_slug: string;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [progress, setProgress] = useState<ProgressItem[]>([]);
  const [loading, setLoading] = useState(true);

  // All hooks must be called before any early returns (React rules of hooks)
  const completedLessons = useMemo(
    () => progress.filter(p => p.status === 'COMPLETED').length,
    [progress]
  );
  const inProgressLessons = useMemo(
    () => progress.filter(p => p.status === 'IN_PROGRESS').length,
    [progress]
  );
  const recentActivity = useMemo(
    () => progress.slice(0, 5),
    [progress]
  );
  // Get the current track from recent activity (most recent track worked on)
  const currentTrackSlug = useMemo(
    () => progress[0]?.track_slug,
    [progress]
  );

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadDashboard() {
    try {
      const [tracksData, progressData] = await Promise.all([
        fetchApi<{ tracks: Track[] }>('/api/tracks'),
        fetchApi<{ progress: ProgressItem[] }>('/api/progress')
      ]);
      setTracks(tracksData.tracks);
      setProgress(progressData.progress);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-3">
          Welcome back{user?.name ? `, ${user.name}` : ''}
        </h1>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="flex items-center gap-2">
            <span className="text-accent-green font-semibold text-lg tabular-nums">{completedLessons}</span>
            <span className="text-gray-400">completed</span>
          </span>

          <span className="text-gray-700">|</span>

          <span className="flex items-center gap-2">
            <span className="text-accent-blue font-semibold text-lg tabular-nums">{inProgressLessons}</span>
            <span className="text-gray-400">in progress</span>
          </span>
        </div>
      </div>

      {/* Streak at Risk Banner - shown when streak needs attention */}
      <StreakRiskBanner />

      {/* Daily Insight - Full width for prominence */}
      <DailyInsight />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-background-secondary rounded-xl border border-gray-800">
            <div className="p-5 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white">Recent Activity</h2>
            </div>
            {recentActivity.length === 0 ? (
              <div className="p-8 text-center">
                <BookOpen className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400 mb-4">No activity yet. Start your first lesson!</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent-blue text-slate-900 rounded-lg hover:bg-blue-500 transition-colors"
                >
                  Browse Tracks
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {recentActivity.map((item, i) => (
                  <Link
                    key={i}
                    to={`/lessons/${item.lesson_id}`}
                    className="flex items-center gap-4 p-4 hover:bg-gray-800/50 transition-colors"
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${
                        item.status === 'COMPLETED'
                          ? 'bg-accent-green'
                          : 'bg-accent-blue'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">
                        {item.lesson_title}
                      </p>
                      <p className="text-sm text-gray-400 truncate">
                        {item.course_title} • {item.track_title}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(item.last_updated).toLocaleDateString()}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Daily Workout */}
          <DailyWorkout />

          {/* Connection Mode Widgets - Only one shows based on mode */}
          <CohortWidget trackSlug={currentTrackSlug} />
          <BuddyWidget currentTrackSlug={currentTrackSlug} />

          {/* Streak Panel */}
          <StreakPanel />

          {/* Quick Links */}
          <div className="bg-background-secondary rounded-xl border border-gray-800 p-5">
            <h3 className="font-semibold text-white mb-4">Quick Links</h3>
            <div className="space-y-2">
              {tracks.slice(0, 3).map(track => (
                <Link
                  key={track.id}
                  to={`/tracks/${track.slug}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <BookOpen className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-300 truncate">{track.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
